<template>
  <div :class="['v2-form-field', { 'v2-form-field--inline': inline }]">
    <template v-if="field.fieldType === 'LIST'">
      <BaseSelect
        v-if="!field.allowMultiple"
        :value="normalizedValue"
        :label="labelText"
        :required="Boolean(field.required)"
        :options="field.options || []"
        :error="error"
        :placeholder="field.placeholder || 'Selecciona…'"
        @input="$emit('update', $event)"
        @change="$emit('update', $event)"
      />
      <div v-else class="v2-form-field__multi">
        <label class="v2-field__label">
          {{ labelText }}
          <span v-if="field.required" class="v2-field__required">*</span>
        </label>
        <div class="v2-form-field__chips" role="group">
          <button
            v-for="option in field.options || []"
            :key="String(option)"
            type="button"
            class="v2-form-field__chip"
            :class="{ 'is-active': isMultiSelected(option) }"
            @click="toggleMulti(option)"
          >
            {{ option }}
          </button>
        </div>
        <p v-if="error" class="v2-field__error" role="alert">{{ error }}</p>
      </div>
    </template>

    <BaseTextarea
      v-else-if="field.fieldType === 'TEXTAREA'"
      :value="value"
      :placeholder="field.placeholder || ''"
      :required="Boolean(field.required)"
      :disabled="disabled"
      :rows="3"
      :error="Boolean(error)"
      @input="$emit('update', $event)"
    />

    <BaseInput
      v-else
      :value="value"
      :type="inputType"
      :label="labelText"
      :placeholder="field.placeholder || ''"
      :required="Boolean(field.required)"
      :disabled="disabled"
      :error="error"
      @input="$emit('update', $event)"
    />
  </div>
</template>

<script>
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import BaseSelect from "@/components/memberDashboardV2/ui/BaseSelect.vue";
import BaseTextarea from "@/components/memberDashboardV2/ui/BaseTextarea.vue";

const DATE_TYPES = new Set(["DATE"]);
const NUMBER_TYPES = new Set(["NUMBER"]);

export default {
  name: "FormFieldRenderer",
  components: { BaseInput, BaseSelect, BaseTextarea },
  props: {
    field: { type: Object, required: true },
    value: { default: "" },
    error: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
  },
  computed: {
    labelText() {
      return this.field.required
        ? `${this.field.name || this.field.formattedName} *`
        : this.field.name || this.field.formattedName;
    },
    inputType() {
      if (!this.field.fieldType) return "text";
      if (DATE_TYPES.has(this.field.fieldType)) return "date";
      if (NUMBER_TYPES.has(this.field.fieldType)) return "number";
      if (this.field.fieldType === "EMAIL") return "email";
      if (this.field.fieldType === "PHONE") return "tel";
      return "text";
    },
    normalizedValue() {
      if (this.field.fieldType === "LIST" && !this.field.allowMultiple) {
        const v = this.value;
        if (v === null || v === undefined) return "";
        return String(v);
      }
      return this.value === null || this.value === undefined ? "" : this.value;
    },
  },
  methods: {
    isMultiSelected(option) {
      const v = this.value;
      if (Array.isArray(v)) return v.indexOf(option) !== -1;
      return false;
    },
    toggleMulti(option) {
      const current = Array.isArray(this.value) ? [...this.value] : [];
      const idx = current.indexOf(option);
      if (idx === -1) {
        current.push(option);
      } else {
        current.splice(idx, 1);
      }
      this.$emit("update", current);
    },
  },
};
</script>

<style scoped>
.v2-form-field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.v2-form-field--inline {
  display: inline-flex;
  min-width: 0;
}

.v2-form-field__multi {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  width: 100%;
}

.v2-form-field__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: var(--space-2);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  min-height: 44px;
}

.v2-form-field__chip {
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) var(--space-3);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-muted);
  background-color: var(--color-neutral-50);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast);
}

.v2-form-field__chip:hover {
  border-color: var(--color-amber);
  color: var(--color-text);
}

.v2-form-field__chip.is-active {
  background-color: var(--color-amber);
  border-color: var(--color-amber);
  color: var(--color-amber-on);
  font-weight: var(--fw-semibold);
}

.v2-form-field__chip:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-field__label {
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.v2-field__required {
  color: var(--color-error);
}

.v2-field__error {
  font-size: var(--fs-caption);
  color: var(--color-error);
  margin: 0;
}
</style>
